import { useEffect, useMemo, useState } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import { getStockReferences, StockReference } from '@/services/api/inventory'

let cachedReferences: StockReference[] | null = null
let inflightReferences: Promise<StockReference[]> | null = null

function loadStockReferences() {
  if (cachedReferences) return Promise.resolve(cachedReferences)
  if (!inflightReferences) {
    inflightReferences = getStockReferences()
      .then((rows) => {
        cachedReferences = rows
        return rows
      })
      .finally(() => {
        inflightReferences = null
      })
  }
  return inflightReferences
}

interface StockReferenceSelectInputProps {
  type: 'lot' | 'booking'
  value: string
  onChange: (value: string, row?: StockReference) => void
}

const StockReferenceSelectInput = ({
  type,
  value,
  onChange,
}: StockReferenceSelectInputProps) => {
  const [rows, setRows] = useState<StockReference[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    let active = true
    loadStockReferences()
      .then((references) => {
        if (active) setRows(references)
      })
      .catch(() => {
        if (active) setLoadFailed(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const options = useMemo(() => {
    const seen = new Set<string>()
    return rows.filter((row) => {
      const reference = type === 'lot' ? row.batchNumber : row.bookingNumber
      const normalized = reference?.trim().toLowerCase()
      if (!normalized || seen.has(normalized)) return false
      seen.add(normalized)
      return true
    })
  }, [rows, type])

  const label = type === 'lot' ? 'lot/batch' : 'booking'

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(event) => {
          const selected = options.find((row) => {
            const reference = type === 'lot' ? row.batchNumber : row.bookingNumber
            return reference === event.target.value
          })
          onChange(event.target.value, selected)
        }}
        disabled={loading || loadFailed}
        className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
        style={{
          fontFamily: 'Lexend Deca',
          fontSize: '12px',
          height: '40px',
          border: '1px solid #E9E9EA',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
        }}
        aria-label={type === 'lot' ? 'Lot or batch' : 'Booking'}
      >
        <option value="">
          {loading
            ? `Loading Zacpak ${label}s...`
            : loadFailed
              ? `Zacpak ${label}s unavailable`
              : `Select Zacpak ${label}`}
        </option>
        {value && !options.some((row) =>
          (type === 'lot' ? row.batchNumber : row.bookingNumber) === value
        ) && (
          <option value={value}>{value} (existing record)</option>
        )}
        {options.map((row) => {
          const reference =
            (type === 'lot' ? row.batchNumber : row.bookingNumber) as string
          const context = [row.product, row.containerNumber, row.location]
            .filter(Boolean)
            .join(' — ')
          return (
            <option key={`${type}-${row.id}-${reference}`} value={reference}>
              {reference}{context ? ` — ${context}` : ''}
            </option>
          )
        })}
      </select>
      <FiChevronDown
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ width: 16, height: 16, color: '#212126', opacity: 0.8 }}
      />
    </div>
  )
}

export default StockReferenceSelectInput
